import React, { useEffect, useRef, useState } from 'react'
import { Autocomplete, Box, Chip, TextField } from '@mui/material'

import { dsColorNew } from '@palette'

/**
 * Multi-select picker for the markets (ISO-2 country codes) the tenant
 * has scrapes in. Renders flag emojis derived client-side from the code.
 *
 * On first load, if `value` is empty and the catalog returns at least
 * one market, the parent's `onChange` is invoked with the primary one
 * (the market with the most quotes). The behaviour can be opted out
 * by passing `autoSelectPrimary={false}` — useful in places that want
 * to preserve a deliberate "no filter / all markets" choice.
 *
 *   <MarketSlicer value={['ES']} onChange={(codes) => ...}/>
 */
export default function MarketSlicer({ value = [], onChange,
                                       label = 'Market',
                                       autoSelectPrimary = true }) {
	const [options, setOptions] = useState([])
	const seededRef = useRef(false)

	useEffect(() => {
		fetch('/api/my-business/catalog/markets', { credentials: 'include' })
			.then(r => r.ok ? r.json() : [])
			.then((rows) => {
				const sorted = (rows || []).slice().sort((a, b) => {
					if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1
					return (b.n_quotes || 0) - (a.n_quotes || 0)
				})
				setOptions(sorted)
				if (autoSelectPrimary && !seededRef.current
				    && value.length === 0
				    && sorted.length > 0) {
					seededRef.current = true
					const primary = sorted.find(o => o.is_primary) || sorted[0]
					if (primary) onChange([primary.code])
				}
			})
			.catch(() => setOptions([]))
	// onChange/value intentionally omitted — we only seed once on mount.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const selected = options.filter(o => value.includes(o.code))

	return (
		<Autocomplete multiple size="small" sx={{ minWidth: 200 }}
		              options={options}
		              value={selected}
		              getOptionLabel={(o) => `${flagOf(o.code)} ${o.code}`}
		              isOptionEqualToValue={(a, b) => a.code === b.code}
		              onChange={(_, v) => onChange(v.map(x => x.code))}
		              renderOption={(props, o) => (
			              <Box component="li" {...props} key={o.code}>
				              <Box display="flex" alignItems="center" gap={1} width="100%">
					              <Box sx={{ fontSize: 16, lineHeight: 1 }}>
						              {flagOf(o.code)}
					              </Box>
					              <Box flex={1} sx={{ fontWeight: 600 }}>
						              {o.code}
						              <Box component="span"
						                   sx={{ ml: 0.75, fontWeight: 400,
						                          color: dsColorNew.textMuted,
						                          fontSize: 12 }}>
							              {nameOf(o.code)}
						              </Box>
					              </Box>
					              {o.is_primary && (
						              <Chip size="small" label="MAIN"
						                    sx={{ height: 18, fontSize: 11, fontWeight: 700, bgcolor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue }}/>
					              )}
					              <Box sx={{ color: dsColorNew.textMuted, fontSize: 12,
					                          fontVariantNumeric: 'tabular-nums' }}>
						              {fmtCount(o.n_quotes)}
					              </Box>
				              </Box>
			              </Box>
		              )}
		              renderTags={(items, getProps) => items.map((o, i) => (
			              <Chip {...getProps({ index: i })} size="small" key={o.code}
			                    label={`${flagOf(o.code)} ${o.code}`}
			                    sx={{ fontWeight: 600 }}/>
		              ))}
		              renderInput={(params) => (
			              <TextField {...params} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} label={label}
			                         placeholder={selected.length ? '' : 'All'}/>
		              )}/>
	)
}

// --- helpers ----------------------------------------------------------------

/** Convert an ISO-2 country code into its flag emoji. */
export function flagOf(code) {
	if (!code || code.length !== 2) return ''
	const base = 127397 // 0x1F1E6 minus 'A'.charCodeAt(0)
	const c0 = code.toUpperCase().charCodeAt(0)
	const c1 = code.toUpperCase().charCodeAt(1)
	if (c0 < 65 || c0 > 90 || c1 < 65 || c1 > 90) return ''
	return String.fromCodePoint(base + c0) + String.fromCodePoint(base + c1)
}

/** Country name via Intl.DisplayNames with a sensible fallback. */
let _displayNames
export function nameOf(code) {
	if (!code) return ''
	try {
		if (!_displayNames) {
			_displayNames = new Intl.DisplayNames(['en'], { type: 'region' })
		}
		return _displayNames.of(code.toUpperCase()) || code
	} catch (e) {
		return code
	}
}

function fmtCount(n) {
	if (n == null) return ''
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
	if (n >= 1_000)     return `${Math.round(n / 1_000)}k`
	return String(n)
}
